import React, { useState, useRef, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import SignatureCanvas from 'react-signature-canvas';
import { 
  PenTool, Type, Calendar, FileText, Check, X, 
  ChevronLeft, ChevronRight, ZoomIn, ZoomOut,
  RotateCcw, CheckCircle2
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from 'sonner';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const FIELD_CONFIGS = {
  signature: { label: 'Signature', icon: PenTool, color: '#f59e0b', bgColor: '#fef3c7' },
  initial: { label: 'Initial', icon: Type, color: '#10b981', bgColor: '#d1fae5' },
  date: { label: 'Date', icon: Calendar, color: '#3b82f6', bgColor: '#dbeafe' },
  text: { label: 'Text', icon: FileText, color: '#8b5cf6', bgColor: '#ede9fe' }
};

const FieldSigningViewer = ({ 
  pdfUrl, 
  fields, 
  filledFields,
  onFieldFill,
  currentSignature, // Pre-captured signature for "apply all"
  currentInitial,
  userName
}) => {
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [selectedField, setSelectedField] = useState(null);
  const [showFieldDialog, setShowFieldDialog] = useState(false);
  const sigCanvasRef = useRef(null);
  const [signatureMethod, setSignatureMethod] = useState('draw');
  const [typedValue, setTypedValue] = useState('');
  const pageRef = useRef(null);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    // Find first page with unfilled fields
    const unfilledField = fields.find(f => !filledFields[f.id]);
    if (unfilledField) {
      setCurrentPage(unfilledField.page);
    }
  };

  const getFieldStyle = (fieldType) => {
    return FIELD_CONFIGS[fieldType] || FIELD_CONFIGS.signature;
  };

  const isFieldFilled = (fieldId) => {
    return !!filledFields[fieldId];
  };

  const handleFieldClick = (field) => {
    if (isFieldFilled(field.id)) return;
    
    setSelectedField(field);
    setTypedValue('');
    
    // Auto-fill date fields
    if (field.field_type === 'date') {
      const today = new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
      onFieldFill(field.id, { value: today });
      toast.success('Date filled');
      return;
    }
    
    // For signature/initial, check if we have a pre-captured one
    if (field.field_type === 'signature' && currentSignature) {
      onFieldFill(field.id, { signature_data: currentSignature });
      toast.success('Signature applied');
      return;
    }
    
    if (field.field_type === 'initial' && currentInitial) {
      onFieldFill(field.id, { signature_data: currentInitial });
      toast.success('Initial applied');
      return;
    }
    
    // Otherwise show the dialog
    setShowFieldDialog(true);
  };

  const handleFieldSubmit = () => {
    if (!selectedField) return;
    
    let fieldData = {};
    
    if (selectedField.field_type === 'signature' || selectedField.field_type === 'initial') {
      if (signatureMethod === 'draw') {
        if (!sigCanvasRef.current || sigCanvasRef.current.isEmpty()) {
          toast.error(`Please ${selectedField.field_type === 'signature' ? 'draw your signature' : 'write your initials'}`);
          return;
        }
        fieldData.signature_data = sigCanvasRef.current.toDataURL('image/png');
      } else {
        if (!typedValue.trim()) {
          toast.error('Please type your name');
          return;
        }
        // Create image from typed text
        const canvas = document.createElement('canvas');
        canvas.width = 300;
        canvas.height = 80;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.font = selectedField.field_type === 'initial' ? '28px "Brush Script MT", cursive' : 'italic 32px "Brush Script MT", cursive';
        ctx.fillStyle = '#1f2937';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(typedValue, canvas.width / 2, canvas.height / 2);
        fieldData.signature_data = canvas.toDataURL('image/png');
      }
    } else if (selectedField.field_type === 'text') {
      if (!typedValue.trim()) {
        toast.error('Please enter text');
        return;
      }
      fieldData.value = typedValue;
    } else if (selectedField.field_type === 'date') {
      fieldData.value = typedValue || new Date().toLocaleDateString();
    }
    
    onFieldFill(selectedField.id, fieldData);
    setShowFieldDialog(false);
    setSelectedField(null);
    toast.success(`${getFieldStyle(selectedField.field_type).label} added`);
  };

  const currentPageFields = fields.filter(f => f.page === currentPage);
  const filledCount = Object.keys(filledFields).length;
  const totalRequired = fields.filter(f => f.required).length;

  return (
    <div className="flex flex-col h-full">
      {/* Progress bar */}
      <div className="p-3 bg-gray-100 border-b">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">
            Fields completed: {filledCount} / {fields.length}
          </span>
          {filledCount === fields.length && (
            <span className="text-sm font-medium text-green-600 flex items-center gap-1">
              <CheckCircle2 className="w-4 h-4" />
              All fields complete
            </span>
          )}
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-gradient-to-r from-teal-500 to-cyan-500 h-2 rounded-full transition-all duration-300"
            style={{ width: `${(filledCount / fields.length) * 100}%` }}
          />
        </div>
      </div>

      {/* PDF Viewer */}
      <div className="flex-1 overflow-auto bg-gray-200 p-4">
        <div ref={pageRef} className="relative inline-block">
          <Document
            file={pdfUrl}
            onLoadSuccess={onDocumentLoadSuccess}
            loading={
              <div className="flex items-center justify-center h-96 w-full bg-white rounded">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
              </div>
            }
          >
            <Page 
              pageNumber={currentPage} 
              scale={scale}
              renderTextLayer={false}
              renderAnnotationLayer={false}
            />
          </Document>

          {/* Render fields */}
          {currentPageFields.map((field) => {
            const style = getFieldStyle(field.field_type);
            const Icon = style.icon;
            const filled = isFieldFilled(field.id);
            const fieldFillData = filledFields[field.id];
            
            return (
              <div
                key={field.id}
                className={`absolute border-2 rounded cursor-pointer transition-all ${
                  filled 
                    ? 'border-green-500 bg-green-50/90' 
                    : 'border-dashed hover:border-solid animate-pulse'
                }`}
                style={{
                  left: `${field.x}%`,
                  top: `${field.y}%`,
                  width: `${field.width}%`,
                  height: `${field.height}%`,
                  borderColor: filled ? '#22c55e' : style.color,
                  backgroundColor: filled ? '#f0fdf4ee' : `${style.bgColor}cc`,
                  zIndex: 10
                }}
                onClick={() => handleFieldClick(field)}
                title={filled ? `${style.label} - Completed` : `Click to add ${style.label}`}
              >
                {filled && fieldFillData?.signature_data ? (
                  <img 
                    src={fieldFillData.signature_data} 
                    alt={style.label}
                    className="w-full h-full object-contain"
                  />
                ) : filled && fieldFillData?.value ? (
                  <div className="flex items-center justify-center h-full px-2">
                    <span className="text-sm font-medium text-gray-800 truncate">
                      {fieldFillData.value}
                    </span>
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full gap-1">
                    <Icon className="w-4 h-4" style={{ color: style.color }} />
                    <span className="text-xs font-medium" style={{ color: style.color }}>
                      {field.label || style.label}
                    </span>
                  </div>
                )}
                
                {filled && (
                  <div className="absolute -top-2 -right-2 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                    <Check className="w-3 h-3 text-white" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Page controls */}
      <div className="flex items-center justify-between p-3 bg-gray-100 border-t">
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => setScale(s => Math.max(0.5, s - 0.1))}>
            <ZoomOut className="w-4 h-4" />
          </Button>
          <span className="text-sm text-gray-600 min-w-[60px] text-center">{Math.round(scale * 100)}%</span>
          <Button size="sm" variant="outline" onClick={() => setScale(s => Math.min(2, s + 0.1))}>
            <ZoomIn className="w-4 h-4" />
          </Button>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage <= 1}
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-sm text-gray-600">Page {currentPage} of {numPages || '?'}</span>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setCurrentPage(p => Math.min(numPages, p + 1))}
            disabled={currentPage >= numPages}
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
        
        <div className="text-sm text-gray-500">
          {currentPageFields.filter(f => isFieldFilled(f.id)).length} / {currentPageFields.length} on this page
        </div>
      </div>

      {/* Field input dialog */}
      <Dialog open={showFieldDialog} onOpenChange={setShowFieldDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedField && (
                <>
                  {React.createElement(getFieldStyle(selectedField.field_type).icon, {
                    className: "w-5 h-5",
                    style: { color: getFieldStyle(selectedField.field_type).color }
                  })}
                  Add {getFieldStyle(selectedField.field_type).label}
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {selectedField && (selectedField.field_type === 'signature' || selectedField.field_type === 'initial') && (
            <div className="space-y-4">
              <Tabs value={signatureMethod} onValueChange={setSignatureMethod}>
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="draw">Draw</TabsTrigger>
                  <TabsTrigger value="type">Type</TabsTrigger>
                </TabsList>
                
                <TabsContent value="draw" className="mt-4">
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-2 bg-white">
                    <SignatureCanvas
                      ref={sigCanvasRef}
                      canvasProps={{
                        className: 'w-full',
                        style: { width: '100%', height: '120px' }
                      }}
                      backgroundColor="white"
                    />
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => sigCanvasRef.current?.clear()}
                    className="mt-2"
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Clear
                  </Button>
                </TabsContent>
                
                <TabsContent value="type" className="mt-4">
                  <div className="space-y-3">
                    <Input
                      value={typedValue}
                      onChange={(e) => setTypedValue(e.target.value)}
                      placeholder={selectedField.field_type === 'initial' ? 'Your initials' : 'Your full name'}
                    />
                    <div className="border rounded-lg p-4 bg-gray-50">
                      <p className="text-sm text-gray-500 mb-2">Preview:</p>
                      <p 
                        className="text-2xl text-center text-gray-800"
                        style={{ fontFamily: '"Brush Script MT", cursive', fontStyle: 'italic' }}
                      >
                        {typedValue || (selectedField.field_type === 'initial' ? 'AB' : 'Your Name')}
                      </p>
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          )}
          
          {selectedField && selectedField.field_type === 'text' && (
            <div className="space-y-3">
              <Label>Enter text</Label>
              <Input
                value={typedValue}
                onChange={(e) => setTypedValue(e.target.value)}
                placeholder="Enter text..."
              />
            </div>
          )}
          
          {selectedField && selectedField.field_type === 'date' && (
            <div className="space-y-3">
              <Label>Date</Label>
              <Input
                type="date"
                value={typedValue}
                onChange={(e) => setTypedValue(e.target.value)}
              />
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFieldDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleFieldSubmit}>
              <Check className="w-4 h-4 mr-2" />
              Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FieldSigningViewer;
